import { useSearchParams } from 'react-router-dom'
import { MigrationLayout } from '../../components/MigrationLayout'
import { getErrorMessage, getPartnerDisplayName } from '../../utils/migrationApi'
import { config } from '../../config'

export function LinkErrorPage() {
  const [searchParams] = useSearchParams()
  const errorType = searchParams.get('error')
  const partner = searchParams.get('partner') || 'swgemu'
  const partnerDisplayName = getPartnerDisplayName(partner)

  const { message, canRetry, showLogin } = getErrorMessage(errorType)

  const handleTryAgain = () => {
    // For retry scenarios, send them back to the partner's signup page
    const partnerConfig = config.partnerUrls[partner.toLowerCase() as keyof typeof config.partnerUrls]
    if (partnerConfig?.signupUrl) {
      window.location.href = partnerConfig.signupUrl
    } else {
      // Fallback to main site if no partner-specific URL
      window.location.href = config.homeUrl
    }
  }

  const handleGoToLogin = () => {
    window.location.href = `${config.apiUrl}/auth/login`
  }

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@swgrealms.com?subject=Account Migration Issue'
  }

  return (
    <MigrationLayout>
      <div className="auth-page">
        <div className="auth-content">
          <div className="auth-card">
            <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⚠️</div>
            <h1 style={{ color: '#ff6b6b' }}>Account Migration Issue</h1>
            <p>We ran into a problem linking your {partnerDisplayName} account</p>

            <div className="auth-explanation">
              <h3 style={{ color: '#ff6b6b' }}>What happened?</h3>
              <div className="error-message">
                {message}
              </div>

              <h3>What can you do?</h3>
              <div className="action-buttons">
                {showLogin && (
                  <button
                    className="auth-action-btn primary"
                    onClick={handleGoToLogin}
                  >
                    Go to Login
                  </button>
                )}

                {canRetry && (
                  <button
                    className="auth-action-btn primary"
                    onClick={handleTryAgain}
                  >
                    {errorType === 'server_error' ? 'Try Again' : `Return to ${partnerDisplayName} Registration`}
                  </button>
                )}

                {!showLogin && !canRetry && (
                  <button
                    className="auth-action-btn secondary"
                    onClick={handleTryAgain}
                  >
                    Return to {partnerDisplayName} Registration
                  </button>
                )}

                <button
                  className="auth-action-btn secondary"
                  onClick={handleContactSupport}
                >
                  Contact Support
                </button>
              </div>

              {errorType === 'user_exists' && (
                <div className="auth-note">
                  <strong>Already have an account?</strong> If you forgot your password, you can reset it on the login page.
                </div>
              )}

              {errorType === 'expired_token' && (
                <div className="auth-note">
                  <strong>Links expire quickly for security.</strong> Just get a fresh link from {partnerDisplayName} and try again.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .auth-page {
          position: relative;
          min-height: calc(100vh - 80px);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: url('/swg_login_4x3.png') no-repeat center center fixed;
          background-size: cover;
          background-color: #000;
        }

        .auth-page::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.4);
          z-index: 1;
        }

        .auth-content {
          text-align: center;
          z-index: 10;
          position: relative;
        }

        .auth-card {
          background: rgba(10, 25, 47, 0.85);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(0, 212, 255, 0.3);
          border-radius: 12px;
          padding: 48px 40px;
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.37),
            inset 0 0 20px rgba(0, 212, 255, 0.1);
          max-width: 600px;
        }

        .auth-card h1 {
          color: #ffffff;
          margin-bottom: 8px;
          font-size: 28px;
          font-weight: 300;
        }

        .auth-card p {
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 32px;
          font-size: 16px;
        }

        .auth-explanation {
          text-align: left;
          margin-bottom: 32px;
          padding: 24px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          border: 1px solid rgba(0, 212, 255, 0.2);
        }

        .auth-explanation h3 {
          color: rgba(255, 255, 255, 0.9);
          margin-top: 0;
          margin-bottom: 20px;
          font-size: 18px;
          font-weight: 500;
        }

        .error-message {
          background: rgba(255, 99, 132, 0.1);
          border-left: 3px solid #ff6384;
          padding: 16px;
          margin-bottom: 24px;
          border-radius: 6px;
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
          line-height: 1.5;
          border: 1px solid rgba(255, 99, 132, 0.2);
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
        }

        .auth-note {
          background: rgba(0, 212, 255, 0.1);
          border-left: 3px solid #00d4ff;
          padding: 16px;
          border-radius: 6px;
          color: rgba(255, 255, 255, 0.8);
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 16px;
        }

        .auth-note:last-child {
          margin-bottom: 0;
        }

        .auth-note strong {
          color: #00d4ff;
          font-weight: 500;
        }

        .auth-action-btn {
          border: none;
          padding: 14px 24px;
          font-size: 16px;
          font-weight: 500;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: block;
          width: 100%;
        }

        .auth-action-btn.primary {
          background: linear-gradient(135deg, #00a8cc 0%, #0088cc 100%);
          color: #ffffff;
        }

        .auth-action-btn.primary:hover {
          background: linear-gradient(135deg, #00b8dc 0%, #0098dc 100%);
          box-shadow: 0 4px 15px rgba(0, 168, 204, 0.3);
        }

        .auth-action-btn.secondary {
          background: transparent;
          color: #00d4ff;
          border: 1px solid rgba(0, 212, 255, 0.3);
        }

        .auth-action-btn.secondary:hover {
          background: rgba(0, 212, 255, 0.1);
          border-color: rgba(0, 212, 255, 0.6);
        }

        @media (max-width: 768px) {
          .auth-card {
            margin: 0 20px;
            padding: 32px 24px;
            max-width: none;
          }
          
          .auth-card h1 {
            font-size: 24px;
          }
          
          .auth-explanation {
            padding: 20px;
          }

          .error-message {
            padding: 12px;
            font-size: 14px;
          }
        }
      `}</style>
    </MigrationLayout>
  )
}
